import {Helmet} from '@dr.pogodin/react-helmet';
import {Form, FormButton, FormField, FormSubmitHandler} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {useLazyWorkspaceInfoQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {buildUrl, doc360Routes, testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {AuthContainer} from '../../components/AuthContainer/AuthContainer';

type WorkspaceForm = {
  workspace: string;
};

export function Doc360WorkspaceSelection() {
  const [getWorkspaceInfo, {isLoading}] = useLazyWorkspaceInfoQuery();

  const onSubmit: FormSubmitHandler<WorkspaceForm> = async ({workspace}, setErrors) => {
    const workspaceNotFound = () =>
      setErrors([
        {name: 'workspace', message: i18n.t('page.login.notifications.workspaceNotFound')},
      ]);

    await getWorkspaceInfo({workspace})
      .unwrap()
      .then((response) => {
        if (!response.workspace) {
          workspaceNotFound();
          return;
        }

        window.location.href = buildUrl(doc360Routes.initialize, response.workspace, false);
      })
      .catch((error: Error & {status?: number}) => {
        if (error.status === 404) {
          workspaceNotFound();
          return;
        }
        handleApiError(error, {setErrors});
      });
  };

  return (
    <>
      <Helmet>
        <title>{i18n.t('page.login.actions.signUp')}</title>
      </Helmet>

      <AuthContainer>
        <Form onSubmit={onSubmit} experimentalZodSchema={schema}>
          {(control) => (
            <VStack spacing={4}>
              <FormField
                name="workspace"
                type="text"
                isRequired
                control={control}
                label={i18n.t('page.login.labels.workspace')}
                data-testid={testIds.login.loginWorkspace('workspaceName')}
                maxLength={100}
              />
              <FormButton
                isLoading={isLoading}
                control={control}
                type="submit"
                data-testid={testIds.login.loginWorkspace('submitButton')}
                title={i18n.t('general.actions.continue')}
              />
            </VStack>
          )}
        </Form>
      </AuthContainer>
    </>
  );
}

const schema = z.object({
  workspace: z.string().min(1, i18n.t('general.notifications.errorWorkspaceNameRequired')),
});
